// apps/web/src/features/dashboards/china-requests/usePeriodQuery.ts (#1589)
// Local period-selector state → resolved `ChinaRequestsSummaryQuery`.
//
// Default preset is `month`. For non-custom presets only `{ period }` is
// sent and the server derives the MSK window. For `custom`, the two
// native date inputs (`yyyy-mm-dd`) are projected to MSK-midnight ISO
// instants: `from` is inclusive at 00:00 MSK; `to` is EXCLUSIVE, so we
// send the day AFTER the picked to-date at 00:00 MSK. The next-day step
// uses UTC date-part arithmetic (no date library, no browser-tz drift).
import { useMemo, useState } from 'react';
import type { ChinaRequestsPeriodKey, ChinaRequestsSummaryQuery } from '@magimp/shared';

const MSK_OFFSET = '+03:00';

// Add one calendar day to a `yyyy-mm-dd` string via UTC date parts so the
// result is independent of the browser timezone.
function nextDay(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export interface UsePeriodQueryResult {
  period: ChinaRequestsPeriodKey;
  setPeriod: (period: ChinaRequestsPeriodKey) => void;
  customFrom: string;
  setCustomFrom: (value: string) => void;
  customTo: string;
  setCustomTo: (value: string) => void;
  // null while a `custom` window is incomplete or inverted — callers
  // disable the query until the user supplies a valid `from <= to`.
  query: ChinaRequestsSummaryQuery | null;
}

export function usePeriodQuery(): UsePeriodQueryResult {
  const [period, setPeriod] = useState<ChinaRequestsPeriodKey>('month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const query = useMemo<ChinaRequestsSummaryQuery | null>(() => {
    if (period !== 'custom') return { period };
    if (!customFrom || !customTo || customFrom > customTo) return null;
    return {
      period: 'custom',
      from: `${customFrom}T00:00:00${MSK_OFFSET}`,
      to: `${nextDay(customTo)}T00:00:00${MSK_OFFSET}`,
    };
  }, [period, customFrom, customTo]);

  return {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    query,
  };
}
